import React, { useEffect } from 'react'
import Menu, { MenuProps } from 'components/menu'
import { Copy, Delete, ExpandAll, Plus, Restore, Template, Comment, Variable, Lock, Visible } from 'components/icons'
import { INITIAL_CONTEXT_MENU_STATE, TEAM_SCALE_PLAN, ORG_PLAN_TIERS } from 'app-constants'
import { useDataContext } from 'components/document/contexts/data'
import Toggle from 'components/toggle'
import api from 'helpers/api'
import useProject from 'hooks/useProject'
import Flag from 'components/flag'
import Tooltip from 'components/tooltip'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import { IProcessSectionObject } from 'types'

interface SettingsMenuProps extends MenuProps {
  setExportModal: (open: boolean) => void
  addSection: (parentId: string | null) => void
  setRestoreSectionModal: (open: boolean) => void
  setDuplicateModal: (open: boolean) => void
  setArchiveModal: (open: boolean) => void
  setDefaultVariablesModal: (open: boolean) => void
  setViewDuplicatesModal: (open: boolean) => void
}

const Settings: React.FC<SettingsMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  setArchiveModal,
  addSection,
  setRestoreSectionModal,
  setDuplicateModal,
  setDefaultVariablesModal,
  setViewDuplicatesModal
}) => {
  const { project } = useProject()
  const { process, updateProcess, isAdmin } = useDataContext()
  const { displayErrorMessage } = useApplicationStore()
  const { user } = useAuth()
  const [hasDeletedSections, setHasDeletedSections] = React.useState(true)

  const isOnTeamOrScalePlan =
    user?.team?.orgPlanTier === ORG_PLAN_TIERS.TEAM || user?.team?.orgPlanTier === ORG_PLAN_TIERS.SCALE_UP

  useEffect(() => {
    if (process && process.publicId !== '' && !process.isDeleted) {
      api
        .getDeletedProcessSections(process.publicId)
        .then((response) => {
          setHasDeletedSections(response.data.some((section: IProcessSectionObject) => section.deletedAt))
        })
        .catch((error) => {
          setHasDeletedSections(true)
          displayErrorMessage(error)
        })
    }
  }, [process?.publicId, process?.isDeleted])

  const restoreProcess = () => {
    if (project && process) {
      api
        .restoreProcess(process.publicId, {
          projectId: project.publicId,
          context: { projectId: project.publicId }
        })
        .then(() => {
          updateProcess({ ...process, isDeleted: false })
        })
    }
  }

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width}>
      <div className="text-body select-none">
        {isAdmin && process && (
          <div>
            {!process.isDeleted && (
              <div>
                <div
                  className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                  onClick={() => addSection(null)}
                >
                  <Plus />
                  <span className="ml-2.5">Add Section</span>
                </div>
                <div
                  className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                  onClick={() => {
                    setDefaultVariablesModal(true)
                  }}
                >
                  <Variable />
                  <span className="ml-2.5">Default Variables</span>
                </div>

                <div
                  className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                  title="This will allow any user with access to use this document as a template."
                >
                  <Template />
                  <span className="ml-2.5">Set As Template</span>
                  <Toggle
                    aria-label="Set As Template"
                    toggled={process ? process.isTemplate : false}
                    onToggle={(toggle) => {
                      if (process) {
                        if (process.lockedTemplate) {
                          updateProcess({ ...process, isTemplate: toggle, lockedTemplate: false })
                        } else {
                          updateProcess({ ...process, isTemplate: toggle })
                        }
                      }
                    }}
                  />
                </div>

                <div
                  className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                  title="This will allow any user with access to use this document as a template."
                >
                  <Lock />
                  <span className="ml-2.5">Lock Template?</span>
                  {!isOnTeamOrScalePlan && <Flag text={TEAM_SCALE_PLAN} color="var(--color-blue)" />}
                  <Toggle
                    aria-label="Lock Template?"
                    toggled={process ? process.lockedTemplate : false}
                    onToggle={(toggle) => {
                      if (process) {
                        updateProcess({ ...process, lockedTemplate: toggle })
                      }
                    }}
                    disabled={!process.isTemplate}
                  />
                </div>

                <div
                  className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                  title="This will expand all sections of the document by default when the document loads."
                >
                  <ExpandAll />
                  <span className="ml-2.5">Expand Sections By Default</span>
                  <Toggle
                    aria-label="Expand Sections By Default"
                    toggled={process ? process.expandByDefault : false}
                    onToggle={(toggle) => {
                      if (process) {
                        updateProcess({ ...process, expandByDefault: toggle })
                      }
                    }}
                  />
                </div>

                <div
                  className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                  title="This will allow users to comment on sections within this process."
                >
                  <Comment />
                  <span className="ml-2.5">Enable Comments</span>
                  <Toggle
                    aria-label="Enable Comments"
                    toggled={process ? process.allowComments : false}
                    onToggle={(toggle) => {
                      if (process) {
                        updateProcess({ ...process, allowComments: toggle })
                      }
                    }}
                  />
                </div>

                {isAdmin && (
                  <div
                    className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                    onClick={() => setDuplicateModal(true)}
                  >
                    <Copy />
                    <span className="ml-2.5">Duplicate Document</span>
                  </div>
                )}

                {isAdmin && (
                  <div
                    className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                    onClick={() => setViewDuplicatesModal(true)}
                  >
                    <Visible />
                    <span className="ml-2.5">View Duplicated Documents</span>
                  </div>
                )}

                {hasDeletedSections ? (
                  <div
                    className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                    onClick={() => {
                      setRestoreSectionModal(true)
                      setMenuState(INITIAL_CONTEXT_MENU_STATE)
                    }}
                  >
                    <Restore />
                    <span className="ml-2.5">Restore Section</span>
                  </div>
                ) : (
                  <Tooltip content="There are no sections to restore.">
                    <div className="relative flex items-center rounded text-faint select-none transition-all truncate cursor-not-allowed h-8 leading-[32px] py-0 px-2.5">
                      <Restore />
                      <span className="ml-2.5">Restore Section</span>
                    </div>
                  </Tooltip>
                )}

                <div
                  className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                  onClick={() => setArchiveModal(true)}
                >
                  <Delete />
                  <span className="ml-2.5">Archive Document</span>
                </div>
              </div>
            )}

            {process.isDeleted && (
              <div
                className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                onClick={() => restoreProcess()}
              >
                <Restore />
                <span className="ml-2.5">Restore Document</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Menu>
  )
}

export default Settings
